import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Lock, Star, Flag, Trophy } from "lucide-react";
import api from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { ThemedShell, ThemedCard, ThemedProgress } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";

export default function Journey() {
  const navigate = useNavigate();
  const { theme, terms } = useTheme();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/child/journey").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const muted = { color: "var(--t-muted)" };
  // Onderaan = start, bovenaan = hoogste level: keer de volgorde om voor het "klimmen".
  const stops = data ? [...data.stops].reverse() : [];

  return (
    <ThemedShell>
      <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 75%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full font-bold" style={{ color: "var(--t-text)" }} data-testid="journey-back-btn"><ArrowLeft className="w-5 h-5 mr-1" /> {terms.dashboard}</Button>
          {data && <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: "var(--t-surface2)" }} data-testid="journey-xp"><Star className="w-4 h-4" style={{ color: "var(--t-accent)", fill: "var(--t-accent)" }} /><span className="font-heading font-extrabold">{data.xp} {terms.xp}</span></div>}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pt-8 pb-20">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl font-black themed-heading">Mijn avontuur</h1>
          <p className="font-semibold" style={muted}>Verdien {terms.xp} en klim naar de top! 🗺️</p>
        </div>

        {!data ? (
          <div className="flex justify-center py-16"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div>
        ) : (
          <div className="relative" data-testid="journey-path">
            {stops.map((s, i) => {
              const sideLeft = i % 2 === 0;
              const isTop = i === 0;
              return (
                <motion.div key={s.level} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className={`flex items-center gap-4 ${sideLeft ? "flex-row" : "flex-row-reverse"}`}>
                  {/* Node */}
                  <div className="relative flex flex-col items-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center border-4 shadow-lg" data-testid={`journey-stop-${s.level}`}
                      style={s.reached ? { background: "var(--t-accent)", borderColor: "var(--t-surface)", color: "var(--t-on-accent)" } : { background: "var(--t-surface2)", borderColor: "var(--t-border)", color: "var(--t-muted)" }}>
                      {s.current ? (
                        <motion.span animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="text-3xl" data-testid="journey-current">{theme.mascot}</motion.span>
                      ) : isTop ? <Trophy className="w-8 h-8" /> : s.reached ? <Check className="w-8 h-8" strokeWidth={3} /> : <Lock className="w-7 h-7" />}
                    </div>
                    {i < stops.length - 1 && <div className="w-1.5 h-10 my-1 rounded-full" style={{ background: stops[i + 1].reached ? "var(--t-accent)" : "var(--t-surface2)" }} />}
                  </div>
                  {/* Label */}
                  <div className={`flex-1 ${sideLeft ? "text-left" : "text-right"}`}>
                    <p className="font-heading text-lg font-extrabold themed-heading">{terms.level} {s.level} · {s.name}</p>
                    {s.current && data.progress.xp_to_next > 0 ? (
                      <div className={`mt-1.5 ${sideLeft ? "" : "ml-auto"}`} style={{ maxWidth: 220 }}>
                        <ThemedProgress value={data.progress.pct} />
                        <p className="text-xs font-semibold mt-1" style={muted}>Nog {data.progress.xp_to_next} {terms.xp} tot {terms.level} {s.level + 1}</p>
                      </div>
                    ) : s.reached ? (
                      <p className="text-sm font-bold" style={{ color: "var(--t-success)" }}>{isTop ? "Hoogste niveau!" : "Behaald ✓"}</p>
                    ) : (
                      <p className="text-sm font-semibold" style={muted}>Vanaf {s.xp_required} {terms.xp}</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
            <div className="flex justify-center mt-4">
              <span className="flex items-center gap-1.5 text-sm font-bold" style={muted}><Flag className="w-4 h-4" /> Start</span>
            </div>
          </div>
        )}
      </main>
    </ThemedShell>
  );
}
